import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  TrendingUp,
  Shield,
  CreditCard,
  Sparkles,
  BarChart3,
  FileText,
  Brain,
  ChevronRight,
  Zap,
  Search,
} from "lucide-react";

const CablewayLogo = ({ className = "w-8 h-8" }) => (
  <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" className={`${className} animate-logo-glow`}>
    <path d="M2 28 L12 14 L17 18 L26 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="26" cy="6" r="3" fill="currentColor" opacity="0.3">
      <animate attributeName="r" values="3;5;3" dur="2.5s" repeatCount="indefinite" />
      <animate attributeName="opacity" values="0.3;0.6;0.3" dur="2.5s" repeatCount="indefinite" />
    </circle>
    <circle cx="26" cy="6" r="1.5" fill="currentColor" />
    <g>
      <animateMotion dur="4s" repeatCount="indefinite" path="M2,28 L12,14 L17,18 L26,6" keyPoints="0;1" keyTimes="0;1" calcMode="linear" />
      <line x1="0" y1="-1" x2="0" y2="3" stroke="currentColor" strokeWidth="1.2" />
      <rect x="-3" y="3" width="6" height="4" rx="1" fill="currentColor" opacity="0.85" />
    </g>
  </svg>
);

const LandingPage = () => {
  const { login, user } = useAuth();
  const { t, locale, changeLocale, locales } = useI18n();
  const navigate = useNavigate();

  if (user) {
    window.location.href = "/dashboard";
    return null;
  }

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Mountain gradient background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background to-primary/[0.03]" />

        {/* Mountain silhouette at bottom */}
        <svg className="absolute bottom-0 left-0 w-full h-[40vh] opacity-[0.04]" viewBox="0 0 1440 400" preserveAspectRatio="none" fill="none">
          <path d="M0 400 L0 280 L120 200 L240 260 L360 160 L480 220 L600 120 L720 180 L840 80 L960 140 L1080 60 L1200 100 L1320 40 L1440 20 L1440 400 Z" fill="currentColor" className="text-primary" />
        </svg>

        <svg className="absolute bottom-0 left-0 w-full h-[50vh] opacity-[0.06]" viewBox="0 0 1440 500" preserveAspectRatio="none" fill="none">
          <path d="M0 450 Q360 380, 720 250 T1440 50" stroke="currentColor" strokeWidth="2" className="text-primary" fill="none" />
          <path d="M0 480 Q360 420, 720 320 T1440 120" stroke="currentColor" strokeWidth="1" className="text-primary" fill="none" opacity="0.5" />
        </svg>

        <div className="absolute inset-0 grid-bg" />
      </div>

      {/* Glow effects */}
      <div className="absolute top-0 right-1/4 w-[600px] h-[600px] bg-primary/4 blur-[160px] rounded-full pointer-events-none" />
      <div className="absolute top-1/3 left-0 w-[300px] h-[300px] bg-primary/3 blur-[100px] rounded-full pointer-events-none" />

      {/* ===== HERO ANIMATED CABLEWAY ===== */}
      <div className="absolute top-[12vh] left-1/2 -translate-x-1/2 w-[850px] h-[320px] pointer-events-none">
        <svg width="850" height="320" viewBox="0 0 850 320" fill="none" xmlns="http://www.w3.org/2000/svg" className="opacity-[0.09]">
          {/* Cable line */}
          <line x1="30" y1="270" x2="790" y2="30" stroke="hsl(var(--primary))" strokeWidth="2" strokeLinecap="round" />

          {/* Support towers */}
          <line x1="120" y1="232" x2="120" y2="280" stroke="hsl(var(--primary))" strokeWidth="2.5" />
          <line x1="400" y1="132" x2="400" y2="180" stroke="hsl(var(--primary))" strokeWidth="2.5" />
          <line x1="650" y1="60" x2="650" y2="108" stroke="hsl(var(--primary))" strokeWidth="2.5" />

          {/* Mountain silhouette behind */}
          <path d="M0 310 L80 220 L160 260 L280 170 L380 200 L500 120 L620 150 L740 60 L850 20 L850 310 Z" fill="hsl(var(--primary))" opacity="0.25" />

          {/* Peak beacon */}
          <circle cx="790" cy="30" r="6" fill="hsl(var(--primary))" opacity="0.4">
            <animate attributeName="r" values="4;8;4" dur="2s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.3;0.7;0.3" dur="2s" repeatCount="indefinite" />
          </circle>
          <circle cx="790" cy="30" r="3" fill="hsl(var(--primary))" opacity="0.8" />

          {/* Animated gondola climbing */}
          <g className="animate-cableway-hero">
            <line x1="0" y1="-3" x2="0" y2="10" stroke="hsl(var(--primary))" strokeWidth="2" />
            <rect x="-14" y="10" width="28" height="18" rx="3" fill="hsl(var(--primary))" opacity="0.9" />
            <rect x="-10" y="13" width="8" height="8" rx="1" fill="hsl(var(--background))" opacity="0.4" />
            <rect x="2" y="13" width="8" height="8" rx="1" fill="hsl(var(--background))" opacity="0.4" />
          </g>
        </svg>
      </div>

      {/* Header */}
      <header className="relative z-10 flex items-center justify-between px-6 md:px-12 py-6">
        <div className="flex items-center gap-3">
          <CablewayLogo className="w-8 h-8 text-primary" />
          <div className="flex flex-col">
            <span className="text-xl font-bold tracking-tight font-['Barlow_Condensed'] uppercase leading-none">
              Cableway Finance
            </span>
            <span className="text-[10px] uppercase tracking-[0.25em] text-primary/70 font-medium leading-none mt-0.5">
              {t("landing.tagline")}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Language switcher */}
          <div className="flex gap-0.5 bg-card/50 border border-border rounded-md p-0.5">
            {locales.map((loc) => {
              const flags = { en: "🇬🇧", fr: "🇫🇷", es: "🇪🇸" };
              return (
                <button
                  key={loc}
                  onClick={() => changeLocale(loc)}
                  className={`px-2 py-1 text-[10px] font-bold uppercase rounded transition-all flex items-center gap-1 ${
                    locale === loc ? "border border-primary text-primary" : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <span className="text-[11px]">{flags[loc]}</span>
                  {loc.toUpperCase()}
                </button>
              );
            })}
          </div>
          <Button
            onClick={login}
            data-testid="header-login-btn"
            className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase tracking-wider text-xs px-6 py-3 rounded-md transition-all active:scale-95 glow-primary"
          >
            {t("landing.signin")}
          </Button>
        </div>
      </header>

      {/* Hero */}
      <main className="relative z-10 flex flex-col items-center justify-center px-6 pt-12 md:pt-20 pb-8">
        <div className="text-center max-w-5xl">
          <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-full px-4 py-1.5 mb-8">
            <Zap className="w-3.5 h-3.5 text-primary" />
            <span className="text-xs font-bold uppercase tracking-widest text-primary">
              {t("landing.badge")}
            </span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tight uppercase font-['Barlow_Condensed'] mb-6 leading-[0.95]">
            {t("landing.hero1")}<br />
            <span className="text-primary">{t("landing.hero2")}</span>
          </h1>

          <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto mb-4 leading-relaxed">
            {t("landing.sub1")}
          </p>

          <p className="text-sm text-muted-foreground/70 max-w-xl mx-auto mb-10 italic">
            {t("landing.sub2")}
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              onClick={login}
              data-testid="hero-login-btn"
              size="lg"
              className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase tracking-wider px-10 py-6 text-sm rounded-md transition-all active:scale-95 glow-primary"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {t("landing.cta")}
            </Button>
            <span className="text-xs text-muted-foreground/50 uppercase tracking-wide">
              {t("landing.nocredit")}
            </span>
          </div>
        </div>

        {/* Stats */}
        <div className="flex flex-wrap items-center justify-center gap-8 md:gap-14 mt-16 mb-16">
          {[
            { val: t("landing.stat1"), sub: t("landing.stat1sub") },
            { val: t("landing.stat2"), sub: t("landing.stat2sub") },
            { val: t("landing.stat3"), sub: t("landing.stat3sub") },
            { val: t("landing.stat4"), sub: t("landing.stat4sub") },
          ].map((s) => (
            <div key={s.val} className="text-center">
              <p className="text-lg md:text-xl font-bold font-['Barlow_Condensed'] uppercase tracking-tight text-foreground">
                {s.val}
              </p>
              <p className="text-[11px] text-muted-foreground/60 uppercase tracking-widest mt-0.5">
                {s.sub}
              </p>
            </div>
          ))}
        </div>

        {/* Features */}
        <div className="max-w-6xl w-full space-y-10">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-primary mb-4 pl-1">
              {t("landing.trackManage")}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
              <FeatureCard icon={<TrendingUp className="w-5 h-5" />} title={t("landing.feat.everyAsset")} description={t("landing.feat.everyAssetDesc")} />
              <FeatureCard icon={<BarChart3 className="w-5 h-5" />} title={t("landing.feat.charts")} description={t("landing.feat.chartsDesc")} />
              <FeatureCard icon={<CreditCard className="w-5 h-5" />} title={t("landing.feat.credit")} description={t("landing.feat.creditDesc")} />
              <FeatureCard icon={<FileText className="w-5 h-5" />} title={t("landing.feat.reports")} description={t("landing.feat.reportsDesc")} />
            </div>
          </div>

          <div>
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-primary mb-4 pl-1">
              {t("landing.discoverBuild")}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
              <FeatureCard icon={<Brain className="w-5 h-5" />} accent title={t("landing.feat.advisor")} description={t("landing.feat.advisorDesc")} />
              <FeatureCard icon={<Search className="w-5 h-5" />} accent title={t("landing.feat.macro")} description={t("landing.feat.macroDesc")} />
              <FeatureCard icon={<Sparkles className="w-5 h-5" />} accent title={t("landing.feat.defi")} description={t("landing.feat.defiDesc")} />
              <FeatureCard icon={<Shield className="w-5 h-5" />} accent title={t("landing.feat.risk")} description={t("landing.feat.riskDesc")} />
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-20 mb-8 text-center max-w-2xl">
          <h2 className="text-base md:text-lg font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-foreground mb-3">
            {t("landing.bottomTitle")}
          </h2>
          <p className="text-sm text-muted-foreground/70 mb-6">
            {t("landing.bottomSub")}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button
              onClick={login}
              data-testid="bottom-login-btn"
              className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase tracking-wider px-8 py-5 text-xs rounded-md transition-all active:scale-95 glow-primary"
            >
              <ChevronRight className="w-4 h-4 mr-1" />
              {t("landing.bottomCta")}
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/demo")}
              data-testid="bottom-demo-btn"
              className="font-bold uppercase tracking-wider px-8 py-5 text-xs rounded-md border-primary text-primary hover:bg-primary/10"
            >
              {t("landing.tryDemo") || "Try Demo"}
            </Button>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 text-center py-8 text-xs text-muted-foreground/50">
        <p>{t("landing.footer")}</p>
      </footer>
    </div>
  );
};

const FeatureCard = ({ icon, title, description, accent }) => (
  <div className={`bg-card/50 backdrop-blur-sm border rounded-lg p-5 hover:-translate-y-1 transition-transform duration-300 group ${accent ? "border-primary/20" : "border-border"}`}>
    <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 transition-colors ${accent ? "bg-primary/15 text-primary group-hover:bg-primary/25" : "bg-primary/10 text-primary group-hover:bg-primary/20"}`}>
      {icon}
    </div>
    <h3 className="font-semibold text-sm mb-1.5 font-['Barlow_Condensed'] uppercase tracking-wide">
      {title}
    </h3>
    <p className="text-xs text-muted-foreground leading-relaxed">
      {description}
    </p>
  </div>
);

export default LandingPage;
